import { Controller } from "@hotwired/stimulus"

const MODES = ["claim", "auction"]
const AUTOPLAY_MS = 8000
const RESUME_MS = 12000

// Hero carousel: claim queue ↔ timed auction demo, autoplay + manual nav
export default class extends Controller {
  static targets = [
    "claimPanel",
    "auctionPanel",
    "claimIdle",
    "claimClaimed",
    "claimRank",
    "claimButton",
    "auctionIdle",
    "auctionBid",
    "auctionRank",
    "auctionButton",
    "caption",
    "chip",
    "dot",
  ]

  connect() {
    this.claimState = "idle"
    this.auctionState = "idle"
    this.modeIndex = 0
    this.autoplayEnabled = !window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    this.showMode(0)
    if (this.autoplayEnabled) this.startAutoplay()
  }

  disconnect() {
    this.stopAutoplay()
    window.clearTimeout(this.resumeTimer)
  }

  selectMode(event) {
    event.preventDefault()
    const index = MODES.indexOf(event.currentTarget.dataset.mode)
    if (index < 0) return

    this.showMode(index)
    this.pauseAutoplayTemporarily()
  }

  act(event) {
    event.preventDefault()

    if (MODES[this.modeIndex] === "claim") {
      if (this.claimState === "idle") this.claimDibs()
      else this.resetClaim()
    } else if (this.auctionState === "idle") {
      this.placeBid()
    } else {
      this.resetAuction()
    }
  }

  showMode(index) {
    this.modeIndex = index
    const mode = MODES[index]

    this.claimPanelTarget.hidden = mode !== "claim"
    this.auctionPanelTarget.hidden = mode !== "auction"
    this.resetClaim()
    this.resetAuction()
    this.syncModeControls(mode)
    this.updateCaption()
  }

  syncModeControls(mode) {
    this.chipTargets.forEach((chip) => {
      const active = chip.dataset.mode === mode
      chip.classList.toggle("is-active", active)
      chip.setAttribute("aria-selected", active ? "true" : "false")
    })

    this.dotTargets.forEach((dot) => {
      const active = dot.dataset.mode === mode
      dot.classList.toggle("is-active", active)
      dot.setAttribute("aria-selected", active ? "true" : "false")
    })
  }

  claimDibs() {
    if (this.claimState !== "idle") return

    this.claimState = "claimed"
    this.claimIdleTarget.hidden = true
    this.claimClaimedTarget.hidden = false
    this.claimButtonTarget.textContent = "Replay"
    this.updateCaption()

    requestAnimationFrame(() => {
      this.claimRankTarget.classList.add("is-promoted")
    })
  }

  placeBid() {
    if (this.auctionState !== "idle") return

    this.auctionState = "bid"
    this.auctionIdleTarget.hidden = true
    this.auctionBidTarget.hidden = false
    this.auctionButtonTarget.textContent = "Replay"
    this.updateCaption()

    requestAnimationFrame(() => {
      this.auctionRankTarget.classList.add("is-promoted")
    })
  }

  resetClaim() {
    this.claimState = "idle"
    this.claimIdleTarget.hidden = false
    this.claimClaimedTarget.hidden = true
    this.claimRankTarget.classList.remove("is-promoted")
    this.claimButtonTarget.textContent = "dibs!"
    if (MODES[this.modeIndex] === "claim") this.updateCaption()
  }

  resetAuction() {
    this.auctionState = "idle"
    this.auctionIdleTarget.hidden = false
    this.auctionBidTarget.hidden = true
    this.auctionRankTarget.classList.remove("is-promoted")
    this.auctionButtonTarget.textContent = "bid $60"
    if (MODES[this.modeIndex] === "auction") this.updateCaption()
  }

  updateCaption() {
    if (MODES[this.modeIndex] === "claim") {
      this.captionTarget.textContent =
        this.claimState === "idle"
          ? "claim queue. Tap dibs!"
          : "Demo only. No real claim was placed."
      return
    }

    this.captionTarget.textContent =
      this.auctionState === "idle"
        ? "timed auction. Tap bid $60"
        : "Demo only. No real bid was placed."
  }

  startAutoplay() {
    this.stopAutoplay()
    this.autoplayTimer = window.setInterval(() => {
      this.showMode((this.modeIndex + 1) % MODES.length)
    }, AUTOPLAY_MS)
  }

  stopAutoplay() {
    if (this.autoplayTimer) window.clearInterval(this.autoplayTimer)
    this.autoplayTimer = null
  }

  pauseAutoplayTemporarily() {
    if (!this.autoplayEnabled) return

    this.stopAutoplay()
    window.clearTimeout(this.resumeTimer)
    this.resumeTimer = window.setTimeout(() => this.startAutoplay(), RESUME_MS)
  }
}
